import React, { useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const TABS = [
  { id: 'topics', label: 'Topic Ideas' },
  { id: 'trending', label: 'Trending Topics' },
  { id: 'calendar', label: 'Content Calendar' },
  { id: 'types', label: 'Content Types' },
  { id: 'competitors', label: 'Competitor Topics' },
  { id: 'seasonal', label: 'Seasonal Opportunities' },
];

const INDUSTRIES = [
  'General', 'Technology', 'Healthcare', 'Finance', 'Education',
  'Marketing', 'E-commerce', 'Real Estate', 'Travel', 'Food & Beverage',
  'Fitness', 'Fashion', 'Automotive', 'Legal', 'Entertainment',
];

const TopicResearch = () => {
  const [seedKeyword, setSeedKeyword] = useState('');
  const [industry, setIndustry] = useState('General');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('topics');
  const resultsRef = useRef(null);
  const searchInputRef = useRef(null);

  const handleResearch = async (e) => {
    e.preventDefault();
    if (!seedKeyword.trim()) {
      toast.error('Please enter a seed keyword');
      searchInputRef.current?.focus();
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/content-analysis/topic-research', {
        seedKeyword: seedKeyword.trim(),
        industry,
        depth: 10
      });
      if (response.data && response.data.research) {
        setResults(response.data.research);
        toast.success('Topic research completed!');
        setActiveTab('topics');
        setTimeout(() => {
          resultsRef.current?.focus();
        }, 100);
      } else {
        toast.error('No results found');
      }
    } catch (error) {
      console.error('Topic research error:', error);
      toast.error(error.response?.data?.message || 'Failed to complete topic research');
    } finally {
      setLoading(false);
    }
  };

  const handleTabKeyDown = (e, tabId) => {
    const currentIndex = TABS.findIndex(t => t.id === tabId);
    let newIndex;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = (currentIndex + 1) % TABS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIndex = TABS.length - 1;
    } else {
      return;
    }
    setActiveTab(TABS[newIndex].id);
    document.getElementById(`tab-${TABS[newIndex].id}`)?.focus();
  };

  const getDifficultyBadge = (difficulty) => {
    if (!difficulty) return null;
    const d = typeof difficulty === 'string' ? difficulty.toLowerCase() : '';
    let classes = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ';
    if (d === 'easy' || d === 'low') {
      classes += 'bg-green-100 text-green-800';
    } else if (d === 'medium' || d === 'moderate') {
      classes += 'bg-yellow-100 text-yellow-800';
    } else {
      classes += 'bg-red-100 text-red-800';
    }
    return <span className={classes} role="text" aria-label={`Difficulty: ${difficulty}`}>{difficulty}</span>;
  };

  const getScoreBadge = (score) => {
    if (score === undefined || score === null) return null;
    const num = typeof score === 'number' ? score : parseInt(score);
    let classes = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ';
    if (num >= 70) {
      classes += 'bg-green-100 text-green-800';
    } else if (num >= 40) {
      classes += 'bg-yellow-100 text-yellow-800';
    } else {
      classes += 'bg-red-100 text-red-800';
    }
    return <span className={classes} role="text" aria-label={`Score: ${num}`}>{num}</span>;
  };

  const renderTopicIdeas = () => {
    if (!results?.topicIdeas?.length) {
      return <p className="text-gray-500 text-center py-8">No topic ideas found.</p>;
    }
    return (
      <div className="grid gap-4 md:grid-cols-2" role="list" aria-label="Topic ideas">
        {results.topicIdeas.map((topic, i) => (
          <article
            key={i}
            role="listitem"
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-blue-500"
            tabIndex={0}
            aria-label={`Topic: ${topic.title || topic.topic || topic}`}
          >
            <h4 className="text-base font-semibold text-gray-900 mb-2">
              {topic.title || topic.topic || topic}
            </h4>
            {topic.description && (
              <p className="text-sm text-gray-600 mb-3">{topic.description}</p>
            )}
            <div className="flex flex-wrap gap-2 items-center">
              {topic.difficulty && getDifficultyBadge(topic.difficulty)}
              {topic.searchVolume && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" role="text" aria-label={`Search volume: ${topic.searchVolume}`}>
                  Vol: {topic.searchVolume}
                </span>
              )}
              {topic.contentType && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {topic.contentType}
                </span>
              )}
              {topic.score !== undefined && getScoreBadge(topic.score)}
            </div>
          </article>
        ))}
      </div>
    );
  };

  const renderTrending = () => {
    if (!results?.trendingTopics?.length) {
      return <p className="text-gray-500 text-center py-8">No trending topics found.</p>;
    }
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" role="list" aria-label="Trending topics">
        {results.trendingTopics.map((topic, i) => (
          <article
            key={i}
            role="listitem"
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-blue-500"
            tabIndex={0}
            aria-label={`Trending topic: ${topic.title || topic.topic || topic}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-base font-semibold text-gray-900 flex-1">
                {topic.title || topic.topic || topic}
              </h4>
              {topic.trend && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" aria-label={`Trend: ${topic.trend}`}>
                  {topic.trend}
                </span>
              )}
            </div>
            {topic.description && (
              <p className="text-sm text-gray-600 mb-2">{topic.description}</p>
            )}
            {topic.volume && (
              <p className="text-xs text-gray-500">
                <span className="font-medium">Volume:</span> {topic.volume}
              </p>
            )}
          </article>
        ))}
      </div>
    );
  };

  const renderCalendar = () => {
    if (!results?.contentCalendar?.length) {
      return <p className="text-gray-500 text-center py-8">No content calendar data available.</p>;
    }
    return (
      <div className="overflow-x-auto" role="region" aria-label="Content calendar table">
        <table className="w-full text-sm" aria-label="Content calendar">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-700">Week/Date</th>
              <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-700">Topic</th>
              <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-700">Content Type</th>
              <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-700">Priority</th>
              <th scope="col" className="text-left py-3 px-4 font-semibold text-gray-700">Notes</th>
            </tr>
          </thead>
          <tbody>
            {results.contentCalendar.map((item, i) => (
              <tr key={i} className="border-b border-gray-100 hover:bg-blue-50 transition-colors">
                <td className="py-3 px-4 text-gray-700 font-medium">{item.week || item.date || `Week ${i + 1}`}</td>
                <td className="py-3 px-4 text-gray-900 font-medium">{item.topic || item.title}</td>
                <td className="py-3 px-4 text-gray-600">
                  {item.contentType && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      {item.contentType}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4">{item.priority && getDifficultyBadge(item.priority)}</td>
                <td className="py-3 px-4 text-gray-500 text-xs">{item.notes || item.description || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContentTypes = () => {
    if (!results?.contentTypes?.length) {
      return <p className="text-gray-500 text-center py-8">No content type suggestions available.</p>;
    }
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" role="list" aria-label="Content type suggestions">
        {results.contentTypes.map((type, i) => (
          <article
            key={i}
            role="listitem"
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-blue-500"
            tabIndex={0}
            aria-label={`Content type: ${type.type || type.name || type}`}
          >
            <h4 className="text-base font-semibold text-gray-900 mb-2">
              {type.type || type.name || type}
            </h4>
            {type.description && (
              <p className="text-sm text-gray-600 mb-3">{type.description}</p>
            )}
            {type.examples && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-500 mb-1">Examples:</p>
                <div className="flex flex-wrap gap-1">
                  {(Array.isArray(type.examples) ? type.examples : [type.examples]).map((ex, j) => (
                    <span key={j} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                      {ex}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {type.effectiveness !== undefined && (
              <div className="mt-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Effectiveness</span>
                  <span className="text-xs font-medium text-gray-700" aria-label={`Effectiveness: ${type.effectiveness}%`}>{type.effectiveness}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2" role="progressbar" aria-valuenow={type.effectiveness} aria-valuemin={0} aria-valuemax={100} aria-label={`Effectiveness: ${type.effectiveness}%`}>
                  <div
                    className={`h-2 rounded-full transition-all ${type.effectiveness >= 70 ? 'bg-green-500' : type.effectiveness >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(type.effectiveness, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </article>
        ))}
      </div>
    );
  };

  const renderCompetitors = () => {
    if (!results?.competitorTopics?.length) {
      return <p className="text-gray-500 text-center py-8">No competitor topic data available.</p>;
    }
    return (
      <div className="grid gap-4" role="list" aria-label="Competitor topics">
        {results.competitorTopics.map((item, i) => (
          <article
            key={i}
            role="listitem"
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-blue-500"
            tabIndex={0}
            aria-label={`Competitor topic: ${item.topic || item.title || item}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-base font-semibold text-gray-900 mb-1">
                  {item.topic || item.title || item}
                </h4>
                {item.competitor && (
                  <p className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">Source:</span> {item.competitor}
                  </p>
                )}
                {item.description && (
                  <p className="text-sm text-gray-600">{item.description}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1 ml-4">
                {item.difficulty && getDifficultyBadge(item.difficulty)}
                {item.opportunity && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" aria-label={`Opportunity: ${item.opportunity}`}>
                    Opportunity: {item.opportunity}
                  </span>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    );
  };

  const renderSeasonal = () => {
    if (!results?.seasonalOpportunities?.length) {
      return <p className="text-gray-500 text-center py-8">No seasonal opportunities found.</p>;
    }
    return (
      <div className="grid gap-4 md:grid-cols-2" role="list" aria-label="Seasonal opportunities">
        {results.seasonalOpportunities.map((item, i) => (
          <article
            key={i}
            role="listitem"
            className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow focus-within:ring-2 focus-within:ring-blue-500"
            tabIndex={0}
            aria-label={`Seasonal opportunity: ${item.topic || item.title || item}`}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-base font-semibold text-gray-900 flex-1">
                {item.topic || item.title || item}
              </h4>
              {item.season && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" aria-label={`Season: ${item.season}`}>
                  {item.season}
                </span>
              )}
            </div>
            {item.description && (
              <p className="text-sm text-gray-600 mb-2">{item.description}</p>
            )}
            {item.timing && (
              <p className="text-xs text-gray-500">
                <span className="font-medium">Best timing:</span> {item.timing}
              </p>
            )}
            {item.months && (
              <p className="text-xs text-gray-500">
                <span className="font-medium">Months:</span> {Array.isArray(item.months) ? item.months.join(', ') : item.months}
              </p>
            )}
          </article>
        ))}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'topics': return renderTopicIdeas();
      case 'trending': return renderTrending();
      case 'calendar': return renderCalendar();
      case 'types': return renderContentTypes();
      case 'competitors': return renderCompetitors();
      case 'seasonal': return renderSeasonal();
      default: return renderTopicIdeas();
    }
  };


  const TOPIC_FAQ = [
    { q: "What is topic research and why is it important for content marketing?", a: "Topic research is the systematic process of discovering, analyzing, and prioritizing content topics that resonate with your target audience and have strong search potential. It goes beyond keyword research by examining topic clusters, audience questions, content gaps, seasonal trends, and competitive coverage. Effective topic research ensures you create content that attracts organic traffic, engages readers, and builds topical authority." },
    { q: "How does SERPfinity discover trending topics?", a: "SERPfinity analyzes multiple data sources including search trend data, social media signals, news aggregation, People Also Ask patterns, forum discussions, and AI search query trends to identify emerging and trending topics in your niche. Our platform surfaces topics before they peak in popularity, giving you a first-mover advantage in creating content that captures early search demand." },
    { q: "What is a content gap and how do I find one?", a: "A content gap is a topic or question that your target audience is searching for but that is not adequately covered by existing content in your niche or on your website. SERPfinity identifies content gaps by comparing your published topics against competitor coverage, analyzing unanswered questions in search results, and finding topics where existing content fails to fully satisfy user intent." },
    { q: "How does topic research help with topical authority?", a: "Topical authority is built by comprehensively covering all aspects of a subject area. Topic research helps you map out the complete topic landscape for your niche, identifying core pillar topics and supporting subtopics. SERPfinity generates topic clusters with hub-and-spoke content structures that signal deep expertise to search engines and AI platforms, improving rankings across all related keywords." },
    { q: "Can topic research improve AI search visibility?", a: "Absolutely. AI search engines prioritize authoritative, comprehensive sources when generating answers. By systematically researching and covering topics in your niche, you build the topical authority that AI platforms look for when selecting citation sources. SERPfinity identifies which topics AI assistants are most frequently answering and where your content could become the cited authority." },
    { q: "How do I prioritize which topics to create content for?", a: "SERPfinity helps prioritize topics using a multi-factor scoring system that considers search volume potential, keyword difficulty, business relevance, content gap opportunity size, competitive landscape, seasonal timing, AI citation potential, and alignment with your existing topical authority. This data-driven approach ensures you invest content creation resources where they will generate the highest ROI." }
  ];

  return (
    <>
      <Helmet>
        <title>Topic Research Tool | Discover Content Ideas & Trends | SERPfinity</title>
        <meta name="description" content="Research trending topics and content ideas for your niche. Discover what audiences are searching for, identify content gaps, and plan winning content strategies with SERPfinity topic research." />
        <meta name="keywords" content="topic research tool, content ideas generator, trending topics, content gap analysis, content strategy planner, niche research, audience insights, content planning, SERPfinity" />
        <link rel="canonical" href="https://thedigitalhall.club/topic-research" />
        <meta property="og:title" content="Topic Research Tool | SERPfinity" />
        <meta property="og:description" content="Discover trending topics, content ideas, and audience insights for your niche. Plan winning content strategies." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thedigitalhall.club/topic-research" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Topic Research Tool | SERPfinity" />
        <meta name="twitter:description" content="Research trending topics and discover content opportunities. Plan data-driven content strategies for SEO success." />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":TOPIC_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white" role="main">
      {/* Page Header */}
      <header className="mb-6 border-b border-gray-200 pb-4 px-6 pt-6">
        <h1 className="text-2xl font-bold text-gray-900">Topic Research</h1>
        <p className="text-gray-600 mt-1">
          Discover trending topics, content ideas, and strategic opportunities for your niche
        </p>
      </header>

      {/* Search Form */}
      <section className="px-6 mb-6" aria-label="Topic research search">
        <form
          onSubmit={handleResearch}
          className="bg-white border border-gray-200 rounded-lg shadow-sm p-6"
          role="search"
          aria-label="Research topics by keyword"
        >
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <label htmlFor="seed-keyword" className="block text-sm font-medium text-gray-700 mb-1">
                Seed Keyword <span className="text-red-500" aria-hidden="true">*</span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                ref={searchInputRef}
                id="seed-keyword"
                type="text"
                value={seedKeyword}
                onChange={(e) => setSeedKeyword(e.target.value)}
                placeholder="Enter a topic or keyword (e.g., digital marketing)"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                required
                aria-required="true"
                aria-describedby="keyword-help"
                disabled={loading}
              />
              <p id="keyword-help" className="sr-only">
                Enter a keyword or topic to discover related trending topics and content ideas
              </p>
            </div>
            <div className="w-full md:w-48">
              <label htmlFor="industry-select" className="block text-sm font-medium text-gray-700 mb-1">
                Industry
              </label>
              <select
                id="industry-select"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                disabled={loading}
                aria-label="Select industry"
              >
                {INDUSTRIES.map(ind => (
                  <option key={ind} value={ind.toLowerCase()}>{ind}</option>
                ))}
              </select>
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap"
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span>Researching...</span>
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Research Topics</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </section>

      {/* Results Section */}
      <section className="px-6 pb-8" aria-label="Research results">
        {!results && !loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center" role="status">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Start Topic Research</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Enter a seed keyword to discover trending topics, content ideas, and strategic opportunities for your niche.
            </p>
          </div>
        ) : loading ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center" role="status" aria-live="polite" aria-label="Loading results">
            <svg className="animate-spin mx-auto h-10 w-10 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Researching Topics...</h3>
            <p className="text-gray-500">Analyzing keyword data, trends, and competitor content. This may take a moment.</p>
          </div>
        ) : (
          <div
            ref={resultsRef}
            tabIndex={-1}
            className="bg-white border border-gray-200 rounded-lg shadow-sm"
            aria-label="Topic research results"
          >
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 px-4" role="tablist" aria-label="Result categories">
              <nav className="flex flex-wrap -mb-px gap-1" aria-label="Result tabs">
                {TABS.map((tab) => {
                  const isActive = activeTab === tab.id;
                  const dataAvailable = results && (
                    (tab.id === 'topics' && results.topicIdeas?.length) ||
                    (tab.id === 'trending' && results.trendingTopics?.length) ||
                    (tab.id === 'calendar' && results.contentCalendar?.length) ||
                    (tab.id === 'types' && results.contentTypes?.length) ||
                    (tab.id === 'competitors' && results.competitorTopics?.length) ||
                    (tab.id === 'seasonal' && results.seasonalOpportunities?.length)
                  );
                  return (
                    <button
                      key={tab.id}
                      id={`tab-${tab.id}`}
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`tabpanel-${tab.id}`}
                      tabIndex={isActive ? 0 : -1}
                      onKeyDown={(e) => handleTabKeyDown(e, tab.id)}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                        ${isActive
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                        ${!dataAvailable ? 'opacity-50' : ''}
                      `}
                    >
                      {tab.label}
                      {dataAvailable ? (
                        <span className="sr-only"> (has results)</span>
                      ) : (
                        <span className="sr-only"> (no results)</span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Panel */}
            <div
              id={`tabpanel-${activeTab}`}
              role="tabpanel"
              aria-labelledby={`tab-${activeTab}`}
              tabIndex={0}
              className="p-6"
            >
              {renderTabContent()}
            </div>
          </div>
        )}
      </section>

      {/* Live Region for Status Updates */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {loading && 'Loading topic research results...'}
        {results && !loading && 'Topic research results are ready.'}
      </div>
    </main>
      <FAQSection items={TOPIC_FAQ} />
    </>
  );
};

export default TopicResearch;
